import { FC, useState, useMemo, ReactNode } from 'react';
import { Popover, Toggle, Icon, TextInput, Avatar } from '@kontentino/ui';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons/faInfoCircle';
import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch';
import { faFont } from '@fortawesome/pro-regular-svg-icons/faFont';
import { faComment } from '@fortawesome/pro-regular-svg-icons/faComment';
import { faImage } from '@fortawesome/pro-regular-svg-icons/faImage';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faSparkles } from '@fortawesome/pro-regular-svg-icons/faSparkles';
import {
  getRecentLanguages,
  addRecentLanguage,
} from 'app/modules/posts/modules/translation/utils/recentLanguages';
import { useTranslationLanguages } from 'app/modules/posts/modules/translation/hooks/useTranslationLanguages';
import { BrandHubApi } from 'app/modules/brandHub/api/brandHub';
import { queryKey } from 'constants/queryKey';

type LanguageOption = {
  value: string;
  label: string;
};

export type TranslateOptions = {
  postText: boolean;
  firstComment: boolean;
  imageText: boolean;
};

export type DisabledToggles = {
  postText?: boolean;
  firstComment?: boolean;
  imageText?: boolean;
};

type Props = {
  id: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  selectedLanguage: string | null;
  onLanguageSelect: (language: string | null) => void;
  translateOptions?: TranslateOptions;
  onTranslateOptionsChange?: (options: TranslateOptions) => void;
  showOriginalOption?: boolean;
  disabledToggles?: DisabledToggles;
  disabled?: boolean;
  isLoading?: boolean;
  infoText?: string;
  trigger: ReactNode;
  footerContent?: ReactNode;
  showBrandProfileSelector?: boolean;
  selectedBrandProfileId?: number | null;
  onBrandProfileChange?: (brandProfileId: number | null) => void;
};

export const useLanguageLabel = (languageValue: string | null): string => {
  const { t } = useTranslation();
  const { data: languages } = useTranslationLanguages();

  if (!languageValue) {
    return t('translation.original', { defaultValue: 'Original' });
  }
  const label = languages?.find((l) => l.value === languageValue)?.label;

  if (label) {
    return label;
  }

  return languageValue.charAt(0).toUpperCase() + languageValue.slice(1);
};

const LanguageSelectorPopover: FC<Props> = ({
  id,
  isOpen,
  onOpenChange,
  selectedLanguage,
  onLanguageSelect,
  translateOptions,
  onTranslateOptionsChange,
  showOriginalOption = true,
  disabledToggles,
  disabled = false,
  isLoading = false,
  infoText,
  trigger,
  footerContent,
  showBrandProfileSelector = false,
  selectedBrandProfileId,
  onBrandProfileChange,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isBrandProfileOpen, setIsBrandProfileOpen] = useState(false);
  const { data: languages = [] } = useTranslationLanguages();

  const brandProfilesQuery = useQuery(
    queryKey.brandHubProfiles(),
    () => BrandHubApi.getAllBrandProfiles(),
    { enabled: showBrandProfileSelector },
  );

  const brandProfiles = brandProfilesQuery.data ?? [];

  const allLanguages = useMemo((): LanguageOption[] => {
    const recent = getRecentLanguages();
    const recentSet = new Set(recent);

    const languagesArray: LanguageOption[] = languages.map((lang) => ({
      value: lang.value,
      label: lang.label,
    }));

    const recentLanguages = recent
      .map((langValue) => languagesArray.find((l) => l.value === langValue))
      .filter((lang): lang is LanguageOption => lang !== undefined);

    const otherLanguages = languagesArray.filter(
      (lang) => !recentSet.has(lang.value),
    );

    return [...recentLanguages, ...otherLanguages];
  }, [languages]);

  const filteredLanguages = useMemo(() => {
    if (!searchQuery.trim()) return allLanguages;
    const query = searchQuery.toLowerCase();
    return allLanguages.filter((lang) =>
      lang.label.toLowerCase().includes(query),
    );
  }, [allLanguages, searchQuery]);

  const handleLanguageSelect = (languageValue: string | null) => {
    if (languageValue) {
      addRecentLanguage(languageValue);
    }
    onLanguageSelect(languageValue);
    if (!languageValue) {
      onOpenChange(false);
    }
  };

  const handleToggleChange = (field: keyof TranslateOptions) => {
    if (!translateOptions || !onTranslateOptionsChange) return;
    onTranslateOptionsChange({
      ...translateOptions,
      [field]: !translateOptions[field],
    });
  };

  const showToggles = !!translateOptions && !!onTranslateOptionsChange;
  const allTogglesOff =
    showToggles &&
    !translateOptions.postText &&
    !translateOptions.firstComment &&
    !translateOptions.imageText;

  const defaultInfoText = t('translation.chooseLanguageInfo', {
    defaultValue:
      'Select a language to translate your content. Optionally choose a Brand Profile to maintain brand voice and visual identity.',
  });

  return (
    <Popover.Root open={isOpen} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild disabled={disabled}>
        {trigger}
      </Popover.Trigger>

      <Popover.Portal container={document.body}>
        <Popover.Content
          align="end"
          side="bottom"
          sideOffset={4}
          avoidCollisions
          collisionPadding={16}
          className="tw-flex tw-max-h-[min(420px,var(--radix-popover-content-available-height,80vh))] tw-w-[280px] tw-flex-col tw-overflow-hidden tw-rounded-lg tw-border tw-border-grayscale-20 tw-bg-white tw-shadow-lg"
          style={{ zIndex: 1010 }}
          data-cy={`language-selector-content-${id}`}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div className="tw-flex-shrink-0 tw-border-b tw-border-grayscale-20 tw-p-3">
            <div className="tw-flex tw-items-start tw-gap-2">
              <Icon
                icon={faInfoCircle}
                className="tw-mt-1 tw-text-primary-100"
              />
              <span className="tw-text-sm tw-text-grayscale-180">
                {infoText || defaultInfoText}
              </span>
            </div>
          </div>

          <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-px-3 tw-py-2">
            <div className="tw-relative tw-mb-2 tw-flex-shrink-0">
              <TextInput
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('search', { defaultValue: 'Search' })}
                iconBefore={
                  <Icon icon={faSearch} className="tw-text-grayscale-100" />
                }
                data-cy={`language-selector-search-${id}`}
              />
            </div>

            <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto">
              {showOriginalOption && (
                <button
                  type="button"
                  onClick={() => handleLanguageSelect(null)}
                  disabled={isLoading}
                  className={`tw-flex tw-w-full tw-items-center tw-rounded tw-px-3 tw-py-2 tw-text-left tw-text-sm ${
                    !selectedLanguage
                      ? 'tw-bg-primary-100 tw-text-white'
                      : 'tw-text-grayscale-180 hover:tw-bg-grayscale-5'
                  } disabled:tw-opacity-50`}
                  data-cy={`language-selector-original-${id}`}
                >
                  {t('translation.originalLanguage', {
                    defaultValue: 'Original',
                  })}
                </button>
              )}

              {filteredLanguages.map((lang) => (
                <button
                  key={lang.value}
                  type="button"
                  onClick={() => handleLanguageSelect(lang.value)}
                  disabled={isLoading || allTogglesOff}
                  className={`tw-flex tw-w-full tw-items-center tw-rounded tw-px-3 tw-py-2 tw-text-left tw-text-sm ${
                    selectedLanguage === lang.value
                      ? 'tw-bg-primary-100 tw-text-white'
                      : 'tw-text-grayscale-180 hover:tw-bg-grayscale-5'
                  } disabled:tw-opacity-50`}
                  data-cy={`language-selector-lang-${lang.value}-${id}`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>

          {showToggles && (
            <>
              <div className="tw-flex-shrink-0 tw-border-t tw-border-grayscale-20" />
              <div className="tw-flex-shrink-0 tw-p-3">
                <div className="tw-mb-2 tw-text-sm tw-font-medium tw-text-grayscale-100">
                  {t('translation.whatToTranslate', {
                    defaultValue: 'What to translate',
                  })}
                </div>

                <div className="tw-flex tw-flex-col tw-gap-2">
                  <label
                    className={`tw-flex tw-items-center tw-justify-between ${
                      disabledToggles?.postText
                        ? 'tw-cursor-not-allowed tw-opacity-50'
                        : 'tw-cursor-pointer'
                    }`}
                  >
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <Icon icon={faFont} className="tw-text-grayscale-100" />
                      <span className="tw-text-sm tw-text-grayscale-180">
                        {t('translation.postText', {
                          defaultValue: 'Post text',
                        })}
                      </span>
                    </div>
                    <Toggle
                      size="small"
                      checked={translateOptions.postText}
                      onChange={() => handleToggleChange('postText')}
                      disabled={disabledToggles?.postText}
                      data-cy={`language-selector-toggle-post-text-${id}`}
                    />
                  </label>

                  <label
                    className={`tw-flex tw-items-center tw-justify-between ${
                      disabledToggles?.firstComment
                        ? 'tw-cursor-not-allowed tw-opacity-50'
                        : 'tw-cursor-pointer'
                    }`}
                  >
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <Icon
                        icon={faComment}
                        className="tw-text-grayscale-100"
                      />
                      <span className="tw-text-sm tw-text-grayscale-180">
                        {t('translation.firstComment', {
                          defaultValue: 'First comment',
                        })}
                      </span>
                    </div>
                    <Toggle
                      size="small"
                      checked={translateOptions.firstComment}
                      onChange={() => handleToggleChange('firstComment')}
                      disabled={disabledToggles?.firstComment}
                      data-cy={`language-selector-toggle-first-comment-${id}`}
                    />
                  </label>

                  <label
                    className={`tw-flex tw-items-center tw-justify-between ${
                      disabledToggles?.imageText
                        ? 'tw-cursor-not-allowed tw-opacity-50'
                        : 'tw-cursor-pointer'
                    }`}
                  >
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <Icon icon={faImage} className="tw-text-grayscale-100" />
                      <span className="tw-text-sm tw-text-grayscale-180">
                        {t('translation.imageText', {
                          defaultValue: 'Image text',
                        })}
                      </span>
                    </div>
                    <Toggle
                      size="small"
                      checked={translateOptions.imageText}
                      onChange={() => handleToggleChange('imageText')}
                      disabled={disabledToggles?.imageText}
                      data-cy={`language-selector-toggle-image-text-${id}`}
                    />
                  </label>
                </div>
                {allTogglesOff && (
                  <p className="tw-mt-2 tw-text-xs tw-text-danger-100">
                    {t('translation.selectAtLeastOneField', {
                      defaultValue:
                        'Please select at least one field to translate.',
                    })}
                  </p>
                )}
              </div>
            </>
          )}

          {showBrandProfileSelector &&
            onBrandProfileChange &&
            brandProfiles.length > 0 && (
              <>
                <div className="tw-flex-shrink-0 tw-border-t tw-border-grayscale-20" />
                <div className="tw-flex tw-min-h-[52px] tw-flex-col tw-px-3 tw-py-2">
                  <button
                    type="button"
                    onClick={() => setIsBrandProfileOpen((prev) => !prev)}
                    className="tw-flex tw-w-full tw-flex-shrink-0 tw-items-center tw-gap-2 tw-rounded tw-px-1 tw-py-2 tw-text-left tw-text-sm tw-text-grayscale-140 hover:tw-text-grayscale-180"
                  >
                    <Icon icon={faSparkles} className="tw-text-grayscale-100" />
                    <span className="tw-flex-1 tw-truncate">
                      {selectedBrandProfileId
                        ? brandProfiles.find(
                            (p) => Number(p.id) === selectedBrandProfileId,
                          )?.name ??
                          t('translation.brandProfile', {
                            defaultValue: 'Brand Profile',
                          })
                        : t('translation.brandProfile', {
                            defaultValue: 'Brand Profile',
                          })}
                    </span>
                    <Icon
                      icon={faChevronDown}
                      className={`tw-text-grayscale-100 tw-transition-transform tw-duration-200 ${
                        isBrandProfileOpen ? 'tw-rotate-180' : ''
                      }`}
                      size="sm"
                    />
                  </button>
                  {isBrandProfileOpen && (
                    <div className="tw-mt-1 tw-h-[120px] tw-min-h-0 tw-flex-1 tw-overflow-y-auto">
                      <button
                        type="button"
                        onClick={() => onBrandProfileChange(null)}
                        className={`tw-flex tw-w-full tw-items-center tw-rounded tw-px-3 tw-py-2 tw-text-left tw-text-sm ${
                          !selectedBrandProfileId
                            ? 'tw-bg-primary-100 tw-text-white'
                            : 'tw-text-grayscale-180 hover:tw-bg-grayscale-5'
                        }`}
                      >
                        {t('translation.noBrandProfile', {
                          defaultValue: 'None (auto)',
                        })}
                      </button>
                      {brandProfiles.map((profile) => (
                        <button
                          key={profile.id}
                          type="button"
                          onClick={() =>
                            onBrandProfileChange(Number(profile.id))
                          }
                          className={`tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded tw-px-3 tw-py-2 tw-text-left tw-text-sm ${
                            selectedBrandProfileId === Number(profile.id)
                              ? 'tw-bg-primary-100 tw-text-white'
                              : 'tw-text-grayscale-180 hover:tw-bg-grayscale-5'
                          }`}
                          data-cy={`language-selector-brand-profile-${profile.id}`}
                        >
                          <Avatar
                            size={16}
                            name={profile.name}
                            src={profile.logoSrc || undefined}
                            className="tw-shrink-0 tw-rounded"
                          />
                          <span className="tw-truncate">{profile.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

          {footerContent && (
            <>
              <div className="tw-flex-shrink-0 tw-border-t tw-border-grayscale-20" />
              <div className="tw-flex-shrink-0 tw-p-3">{footerContent}</div>
            </>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default LanguageSelectorPopover;
